import { useState } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { ReportedMessage } from '@/mocks/adminModeracionMessages';
import { reportedMessages as initialReported, statusLabels, statusColors } from '@/mocks/adminModeracionMessages';

export default function ReportedMessagesPanel() {
  const [messages, setMessages] = useState<ReportedMessage[]>(initialReported);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReportedMessage['status'] | 'todos'>('todos');
  const [expanded, setExpanded] = useState<string | null>(null);

  const statuses: (ReportedMessage['status'] | 'todos')[] = ['todos', 'pendiente', 'revisado', 'desestimado'];

  const filtered = messages.filter((m) => {
    if (statusFilter !== 'todos' && m.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return m.username.toLowerCase().includes(q) || m.messagePreview.toLowerCase().includes(q) || m.autoLabel.toLowerCase().includes(q);
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const handleHideMessage = (msg: ReportedMessage) => {
    SwalAlert.confirm('¿Ocultar mensaje?', `El mensaje de ${msg.username} será ocultado del chat público.`).then((result) => {
      if (result.isConfirmed) {
        setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, status: 'revisado' as const } : m));
        SwalAlert.toast('Mensaje ocultado correctamente', 'success');
      }
    });
  };

  const handleDismiss = (msg: ReportedMessage) => {
    SwalAlert.confirm('¿Desestimar reporte?', `El reporte contra ${msg.username} será marcado como desestimado.`).then((result) => {
      if (result.isConfirmed) {
        setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, status: 'desestimado' as const } : m));
        SwalAlert.toast('Reporte desestimado', 'success');
      }
    });
  };

  const handleBlockUser = (msg: ReportedMessage) => {
    SwalAlert.confirm('¿Bloquear usuario?', `Se bloqueará permanentemente a ${msg.username} y se ocultarán todos sus mensajes.`).then((result) => {
      if (result.isConfirmed) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        SwalAlert.toast(`${msg.username} bloqueado permanentemente`, 'success');
      }
    });
  };

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Reportes pendientes', value: messages.filter((m) => m.status === 'pendiente').length, icon: 'ri-alert-line', color: 'text-red-500' },
          { label: 'Revisados', value: messages.filter((m) => m.status === 'revisado').length, icon: 'ri-check-double-line', color: 'text-amber-500' },
          { label: 'Desestimados', value: messages.filter((m) => m.status === 'desestimado').length, icon: 'ri-close-circle-line', color: 'text-foreground-400' },
          { label: 'Total reportes', value: messages.length, icon: 'ri-flag-2-line', color: 'text-foreground-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por usuario o contenido..."
            className="w-full pl-8 pr-8 py-2 rounded-xl bg-background-50 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors">
              <i className="ri-close-line text-background-50 text-[8px]"></i>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
                statusFilter === s
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {s === 'todos' ? 'Todos' : statusLabels[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center py-12 gap-2 bg-background-50 border border-background-200 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-flag-2-line text-foreground-300 text-lg"></i>
          </div>
          <p className="text-xs text-foreground-400 font-medium">Sin reportes</p>
          <p className="text-[10px] text-foreground-400">No se encontraron mensajes reportados con esos filtros</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((msg) => (
            <div
              key={msg.id}
              className={`bg-background-50 border rounded-xl overflow-hidden transition-all ${
                msg.status === 'pendiente' ? 'border-red-200 bg-red-50/20' :
                msg.status === 'revisado' ? 'border-amber-200 bg-amber-50/20' :
                'border-background-200'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between px-4 py-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    msg.status === 'pendiente' ? 'bg-red-100 text-red-600' :
                    msg.status === 'revisado' ? 'bg-amber-100 text-amber-600' :
                    'bg-background-200 text-foreground-400'
                  }`}>
                    {msg.avatarLetter}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-xs font-semibold text-foreground-900">{msg.username}</span>
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${statusColors[msg.status]}`}>
                        {statusLabels[msg.status]}
                      </span>
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-background-100 text-foreground-500">
                        <i className="ri-price-tag-3-line text-[9px]"></i>
                        {msg.autoLabel}
                      </span>
                    </div>
                    <p className="text-[10px] text-foreground-600 line-clamp-2">{msg.messagePreview}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[9px] text-foreground-400">
                        <i className="ri-calendar-line mr-0.5"></i>
                        {formatDate(msg.lastReportedAt)}
                      </span>
                      <span className="text-[9px] text-foreground-400">
                        <i className="ri-megaphone-line mr-0.5"></i>
                        {msg.programContext}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-[9px] text-red-500 font-semibold">
                        <i className="ri-flag-2-fill"></i>
                        {msg.reportCount} reportes
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-3">
                  <button
                    onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}
                    className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    title="Ver mensaje completo"
                  >
                    <i className={`${expanded === msg.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 text-xs`}></i>
                  </button>
                  {msg.status === 'pendiente' && (
                    <>
                      <button
                        onClick={() => handleHideMessage(msg)}
                        className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center cursor-pointer hover:bg-red-200 transition-colors"
                        title="Ocultar mensaje"
                      >
                        <i className="ri-eye-off-line text-red-600 text-xs"></i>
                      </button>
                      <button
                        onClick={() => handleBlockUser(msg)}
                        className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center cursor-pointer hover:bg-rose-200 transition-colors"
                        title="Bloquear usuario"
                      >
                        <i className="ri-forbid-2-line text-rose-600 text-xs"></i>
                      </button>
                      <button
                        onClick={() => handleDismiss(msg)}
                        className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center cursor-pointer hover:bg-foreground-200 transition-colors"
                        title="Desestimar reporte"
                      >
                        <i className="ri-close-line text-foreground-500 text-xs"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Reporters */}
              <div className="px-4 pb-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] text-foreground-400">Reportado por:</span>
                  {msg.reportedByUsers.map((user, i) => (
                    <span key={i} className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] bg-background-100 text-foreground-500">
                      {user}
                    </span>
                  ))}
                </div>
              </div>

              {/* Expanded */}
              {expanded === msg.id && (
                <div className="px-4 pb-4 border-t border-background-100 pt-3">
                  <p className="text-[10px] font-semibold text-foreground-400 mb-1.5">Mensaje completo</p>
                  <p className="text-xs text-foreground-800 bg-background-100 rounded-lg px-3 py-2.5 leading-relaxed">
                    {msg.fullMessage}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}